import {
    endorsementCodes,
    endorsementQuestionNumbers,
    endorsementTypes,
    getDefaultEndorsementQuestionAnswers,
} from "@/constants/endorsements";

export function getSelectedPolicyVehicle(order) {
    const policyVehicleId = order?.vehicle?.policyVehicleId;
    if (policyVehicleId == null) {
        return null;
    }

    const selectedId = policyVehicleId.toString();
    return (
        (order?.policy?.vehicles ?? []).find(
            (policyVehicle) =>
                policyVehicle?.id?.toString() === selectedId ||
                policyVehicle?.policyVehicleId?.toString() === selectedId
        ) ?? null
    );
}

export function getSelectedPolicyCoverageCode(order) {
    const coverageCode = getSelectedPolicyVehicle(order)?.coverages?.[0]?.code;
    return coverageCode == null || coverageCode === "" ? null : coverageCode;
}

export function getSelectedPolicyVehicleEndorsements(order) {
    const endorsements = getSelectedPolicyVehicle(order)?.endorsements;
    return Array.isArray(endorsements) ? endorsements.map(getCanonicalEndorsementName) : [];
}

export function hasEndorsementType(endorsements, endorsementType) {
    return (endorsements ?? []).some(
        (endorsement) => getCanonicalEndorsementName(endorsement) === endorsementType
    );
}

export function selectedPolicyVehicleHasOemEndorsement(order) {
    return hasEndorsementType(
        getSelectedPolicyVehicleEndorsements(order),
        endorsementTypes.OEM_APPROVED
    );
}

export function isAffirmativeEndorsementAnswer(value) {
    return value === true || normalizeEndorsementName(value) === "yes";
}

export function selectedAnswerFromValue(value) {
    if (isAffirmativeEndorsementAnswer(value)) {
        return "Yes";
    }
    return value === false || normalizeEndorsementName(value) === "no" ? "No" : "";
}

export function getEndorsementSelectedAnswer(answers, endorsementName) {
    const match = normalizeEndorsementQuestionAnswers(answers).find(
        (answer) => answer.endorsementName === endorsementName
    );
    return match?.selectedAnswer ?? "";
}

export function buildCvrgEndorsementCode(vehicleEndorsements) {
    const codes = [];
    if (hasEndorsementType(vehicleEndorsements, endorsementTypes.EDUCATOR)) {
        codes.push(endorsementCodes.EDUCATOR);
    }
    if (hasEndorsementType(vehicleEndorsements, endorsementTypes.PARKING_GUARD)) {
        codes.push(endorsementCodes.PARKING_GUARD);
    }
    return codes.length ? codes.join(",") : null;
}

export function buildEndorsementQuestionAnswers({
    vehicleEndorsements,
    educatorAnswer,
    parkingGuardAnswer,
    educatorQuestionText,
    parkingGuardQuestionText,
} = {}) {
    const answers = [];
    if (hasEndorsementType(vehicleEndorsements, endorsementTypes.EDUCATOR)) {
        answers.push({
            questionNum: endorsementQuestionNumbers.EDUCATOR,
            endorsementName: endorsementTypes.EDUCATOR,
            questionText: educatorQuestionText || "",
            selectedAnswer: selectedAnswerFromValue(educatorAnswer),
        });
    }
    if (hasEndorsementType(vehicleEndorsements, endorsementTypes.PARKING_GUARD)) {
        answers.push({
            questionNum: endorsementQuestionNumbers.PARKING_GUARD,
            endorsementName: endorsementTypes.PARKING_GUARD,
            questionText: parkingGuardQuestionText || "",
            selectedAnswer: selectedAnswerFromValue(parkingGuardAnswer),
        });
    }
    return answers;
}

export function normalizeEndorsementQuestionAnswers(value) {
    const parsedValue = parseEndorsementQuestionAnswersValue(value);
    return Array.isArray(parsedValue)
        ? parsedValue.map(normalizeEndorsementQuestionAnswerItem).filter(Boolean)
        : getDefaultEndorsementQuestionAnswers();
}

export function coalesceCvrgEndorsementCode(sessionValue, currentValue) {
    return sessionValue ? sessionValue : (currentValue ?? null);
}

export function coalesceEndorsements(sessionValue, currentValue) {
    if (Array.isArray(sessionValue) && sessionValue.length > 0) {
        return [...sessionValue];
    }
    return Array.isArray(currentValue) ? [...currentValue] : [];
}

export function coalesceEndorsementQuestionAnswers(sessionValue, currentValue) {
    const sessionAnswers = normalizeEndorsementQuestionAnswers(sessionValue);
    return sessionAnswers.length > 0
        ? sessionAnswers
        : normalizeEndorsementQuestionAnswers(currentValue);
}

function getCanonicalEndorsementName(endorsementName) {
    const normalizedName = normalizeEndorsementName(endorsementName);
    return (
        Object.values(endorsementTypes).find(
            (type) => normalizeEndorsementName(type) === normalizedName
        ) ?? endorsementName
    );
}

function normalizeEndorsementQuestionAnswerItem(answer) {
    if (!answer || typeof answer !== "object") {
        return null;
    }

    const endorsementName = getCanonicalEndorsementName(answer.endorsementName);
    if (!endorsementName) {
        return null;
    }

    return {
        questionNum: answer.questionNum ?? getEndorsementQuestionNumber(endorsementName),
        endorsementName,
        questionText: answer.questionText ?? "",
        selectedAnswer: selectedAnswerFromValue(answer.selectedAnswer),
    };
}

function getEndorsementQuestionNumber(endorsementName) {
    if (endorsementName === endorsementTypes.EDUCATOR) {
        return endorsementQuestionNumbers.EDUCATOR;
    }
    if (endorsementName === endorsementTypes.PARKING_GUARD) {
        return endorsementQuestionNumbers.PARKING_GUARD;
    }
    return null;
}

function parseEndorsementQuestionAnswersValue(value) {
    if (!value) {
        return null;
    }

    if (typeof value === "string") {
        try {
            const parsedValue = JSON.parse(decodeURIComponent(value));
            return Array.isArray(parsedValue) ? parsedValue : null;
        } catch {
            return null;
        }
    }

    return Array.isArray(value) ? value : null;
}

function normalizeEndorsementName(value) {
    return String(value ?? "")
        .trim()
        .toLowerCase();
}
